/**
 * Trade scenes - one per hazard profile, drawn as technical safety diagrams
 * (vt-863 "hybrid C + B"): blueprint grid, ink line work, dimension lines with
 * figures, hatched solids, verdict badges, a small table where a matrix says
 * more than a picture, and the governing rule on the source line. Vocabulary
 * and rules: `scenes-shared.tsx`; step membership only through `<On>`.
 */

import {
  Blueprint,
  blueprintIds,
  Dim,
  On,
  Panel,
  type SceneProps,
  Solid,
  Verdict,
} from './scenes-shared';

/** Small verdict square for table cells (B-ext style). */
function Sq({ kind, x, y }: { kind: 'ok' | 'no' | 'lim'; x: number; y: number }) {
  const cls = kind === 'ok' ? 'hbB-ok-fill' : kind === 'no' ? 'hbB-no-fill' : 'hbB-warn-fill';
  return <rect className={cls} x={x - 7} y={y - 7} width="14" height="14" rx="2" />;
}

/** Two rails + rungs between two points; the centre line runs from (x1,y1) to (x2,y2). */
function Ladder({
  x1,
  y1,
  x2,
  y2,
  className = 'hbB-draw-heavy',
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  className?: string;
}) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy * 7;
  const ny = ux * 7;
  const rungs: number[] = [];
  for (let t = 20; t < len - 14; t += 20) rungs.push(t);
  const f = (n: number) => n.toFixed(1);
  return (
    <g>
      <g className={className}>
        <line x1={f(x1 + nx)} y1={f(y1 + ny)} x2={f(x2 + nx)} y2={f(y2 + ny)} />
        <line x1={f(x1 - nx)} y1={f(y1 - ny)} x2={f(x2 - nx)} y2={f(y2 - ny)} />
      </g>
      <g className="hbB-draw">
        {rungs.map((t) => (
          <line
            key={t}
            x1={f(x1 + ux * t + nx)}
            y1={f(y1 + uy * t + ny)}
            x2={f(x1 + ux * t - nx)}
            y2={f(y1 + uy * t - ny)}
          />
        ))}
      </g>
    </g>
  );
}

/* ------------------------------------------------------------------ */

/** Hazard → PPE matrix (NV 390/2021 Sb.); step k rings row k and draws its pictogram pair. */
export function PpeMatchScene({ en }: SceneProps) {
  const rows = [
    { hazard: en ? 'Falling object' : 'Pád předmětu', ppe: en ? 'helmet' : 'přilba' },
    { hazard: en ? 'Sparks' : 'Jiskry', ppe: en ? 'goggles' : 'brýle' },
    { hazard: en ? 'Chemicals' : 'Chemikálie', ppe: en ? 'gloves' : 'rukavice' },
  ];
  const cols = [en ? 'Helmet' : 'Přilba', en ? 'Goggles' : 'Brýle', en ? 'Gloves' : 'Rukavice'];
  const colX = [0, 128, 190, 252];
  const colW = [128, 62, 62, 84];
  const rowH = 40;
  const headH = 34;
  return (
    <Blueprint
      id="ppeMatch"
      label={en ? 'Hazard to PPE matrix' : 'Matice riziko → ochranný prostředek'}
      title={en ? 'PPE · hazard → item' : 'OOPP · riziko → prostředek'}
      source="NV 390/2021 Sb. · ZP § 104"
    >
      <g transform="translate(16 36)">
        {cols.map((c, i) => (
          <g key={c}>
            <rect className="hbB-head" x={colX[i + 1]} y="0" width={colW[i + 1]} height={headH} />
            <text className="hbB-soft" x={colX[i + 1] + colW[i + 1] / 2} y="23" textAnchor="middle">
              {c}
            </text>
          </g>
        ))}
        <rect className="hbB-head" x="0" y="0" width={colW[0]} height={headH} />
        <text className="hbB-soft" x="8" y="23">
          {en ? 'Hazard' : 'Riziko'}
        </text>
        {rows.map((r, i) => {
          const y = headH + i * rowH;
          return (
            <g key={r.hazard}>
              <rect className="hbB-cell" x="0" y={y} width={colW[0]} height={rowH} />
              <text className="hbB-lbl" x="8" y={y + 26}>
                {r.hazard}
              </text>
              {cols.map((c, j) => (
                <g key={c}>
                  <rect
                    className="hbB-cell"
                    x={colX[j + 1]}
                    y={y}
                    width={colW[j + 1]}
                    height={rowH}
                  />
                  {j === i ? (
                    <Sq kind="ok" x={colX[j + 1] + colW[j + 1] / 2} y={y + rowH / 2} />
                  ) : (
                    <line
                      className="hbB-thin"
                      x1={colX[j + 1] + colW[j + 1] / 2 - 6}
                      y1={y + rowH / 2}
                      x2={colX[j + 1] + colW[j + 1] / 2 + 6}
                      y2={y + rowH / 2}
                    />
                  )}
                </g>
              ))}
              <On step={`${i + 1}`}>
                <rect
                  className="hbB-focus"
                  x="-4"
                  y={y - 3}
                  width={colX[3] + colW[3] + 8}
                  height={rowH + 6}
                  rx="4"
                />
              </On>
            </g>
          );
        })}
        {/* legend */}
        <Sq kind="ok" x={7} y={headH + 3 * rowH + 20} />
        <text className="hbB-soft" x="20" y={headH + 3 * rowH + 26}>
          {en ? 'required item' : 'nutný prostředek'}
        </text>
      </g>

      {/* pictogram column: hazard → PPE for the active row */}
      <g transform="translate(370 40)">
        <rect className="hbB-card" x="0" y="0" width="94" height="200" rx="6" />
        <On step="1">
          {/* falling brick */}
          <rect className="hbB-ink-soft" x="34" y="14" width="26" height="16" />
          <path className="hbB-no" d="M47 36 v22 m-6 -8 l6 8 l6 -8" />
          {/* helmet: dome + brim */}
          <path className="hbB-draw-heavy" d="M27 108 a20 20 0 0 1 40 0" />
          <line className="hbB-draw-heavy" x1="21" y1="108" x2="73" y2="108" />
          <path className="hbB-thin" d="M47 88 v20" />
          <text className="hbB-lbl" x="47" y="140" textAnchor="middle">
            {rows[0].ppe}
          </text>
          <Verdict kind="ok" x={47} y={170} r={14} />
        </On>
        <On step="2">
          {/* sparks */}
          <g className="hbB-lim">
            <line x1="47" y1="14" x2="47" y2="30" />
            <line x1="31" y1="22" x2="63" y2="22" />
            <line x1="36" y1="11" x2="58" y2="33" />
            <line x1="58" y1="11" x2="36" y2="33" />
          </g>
          <path className="hbB-no" d="M47 40 v18 m-6 -8 l6 8 l6 -8" />
          {/* sealed goggles */}
          <rect className="hbB-draw-heavy" x="21" y="94" width="22" height="18" rx="6" />
          <rect className="hbB-draw-heavy" x="51" y="94" width="22" height="18" rx="6" />
          <line className="hbB-draw" x1="43" y1="103" x2="51" y2="103" />
          <path className="hbB-thin" d="M21 100 h-6 M73 100 h6" />
          <text className="hbB-lbl" x="47" y="140" textAnchor="middle">
            {rows[1].ppe}
          </text>
          <Verdict kind="ok" x={47} y={170} r={14} />
        </On>
        <On step="3">
          {/* flask with drops */}
          <path className="hbB-draw" d="M41 10 v10 l-10 16 h32 l-10 -16 v-10 z" />
          <path className="hbB-water" d="M47 44 c0 -4 4 -7 4 -10 c0 3 4 6 4 10 a4 4 0 0 1 -8 0 z" />
          <path className="hbB-no" d="M47 50 v8 m-6 -8 l6 8 l6 -8" />
          {/* glove */}
          <path
            className="hbB-draw-heavy"
            d="M33 122 v-24 a5 5 0 0 1 10 0 v-8 a5 5 0 0 1 10 0 v8 a5 5 0 0 1 10 0 v10 l6 -8 a4 4 0 0 1 6 5 l-10 17 v0 h-32 z"
          />
          <text className="hbB-lbl" x="47" y="140" textAnchor="middle">
            {rows[2].ppe}
          </text>
          <Verdict kind="ok" x={47} y={170} r={14} />
        </On>
      </g>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Leaning ladder blueprint: too steep ✗ (~85°), 4:1 ≈ 75° ✓ with dims + ≥ 1,1 m overhang, too shallow ✗. */
export function LadderAngleScene({ en }: SceneProps) {
  const ids = blueprintIds('ladderAngle');
  const groundY = 232;
  const topY = 52;
  const wallX = 300;
  const landingY = 96;
  // centre-line base x for each variant; the top always rests on the wall at topY
  const baseRight = wallX - 45; // 180 / 45 = 4 : 1 → ≈ 76°
  const baseSteep = wallX - 16; // ≈ 85°
  const baseFlat = wallX - 120; // ≈ 56°
  const arc = (bx: number) => {
    const dx = wallX - bx;
    const dy = groundY - topY;
    const len = Math.hypot(dx, dy);
    const ex = bx + (dx / len) * 38;
    const ey = groundY - (dy / len) * 38;
    return `M${bx + 38} ${groundY} A38 38 0 0 0 ${ex.toFixed(1)} ${ey.toFixed(1)}`;
  };
  return (
    <Blueprint
      id="ladderAngle"
      label={en ? 'Leaning ladder - angle and overhang' : 'Opěrný žebřík - sklon a přesah'}
      title={en ? 'Leaning ladder · angle 4 : 1' : 'Opěrný žebřík · sklon 4 : 1'}
      source="NV 362/2005 Sb., příl. čl. III"
    >
      {/* ground + wall */}
      <Solid hatch={ids.hatch} x={40} y={groundY} width={380} height={12} edge="top" />
      <Solid hatch={ids.hatch} x={wallX} y={40} width={12} height={groundY - 40} edge="left" />
      <line className="hbB-draw" x1={wallX} y1={landingY} x2={wallX + 30} y2={landingY} />
      <text className="hbB-soft" x={wallX + 16} y={landingY + 20}>
        {en ? 'landing' : 'výstupní úroveň'}
      </text>

      {/* parameter box */}
      <g transform="translate(24 40)">
        <rect className="hbB-cell" x="0" y="0" width="156" height="92" rx="2" />
        <text className="hbB-title" x="10" y="18">
          {en ? 'Parameters' : 'Parametry'}
        </text>
        <text className="hbB-mono" x="10" y="40">
          {en ? 'angle' : 'sklon'}
        </text>
        <text className="hbB-mono" x="146" y="40" textAnchor="end">
          ≈ 75° · 4 : 1
        </text>
        <text className="hbB-mono" x="10" y="60">
          {en ? 'overhang' : 'přesah'}
        </text>
        <text className="hbB-mono" x="146" y="60" textAnchor="end">
          ≥ 1,1 m
        </text>
        <text className="hbB-mono" x="10" y="80">
          h max.
        </text>
        <text className="hbB-mono" x="146" y="80" textAnchor="end">
          5 m
        </text>
      </g>

      {/* 1 - too steep */}
      <On step="1">
        <line className="hbB-ghost" x1={baseRight} y1={groundY} x2={wallX} y2={topY} />
        <Ladder x1={baseSteep} y1={groundY} x2={wallX} y2={topY} />
        <path className="hbB-dim" d={arc(baseSteep)} />
        <text className="hbB-no-txt" x={baseSteep - 6} y={groundY - 12} textAnchor="end">
          ≈ 85°
        </text>
        {/* tips backwards */}
        <path className="hbB-no" d="M276 70 q-30 -8 -52 12 m8 -12 l-8 12 l14 4" />
        <text className="hbB-no-txt" x="212" y="150" textAnchor="end">
          {en ? 'tips back' : 'překlopí se'}
        </text>
        <Verdict kind="no" x={60} y={165} />
      </On>

      {/* 2 - right: 4 : 1 */}
      <On step="2">
        <Ladder x1={baseRight} y1={groundY} x2={wallX} y2={topY} />
        {/* anti-slip feet */}
        <path
          className="hbB-ink"
          d={`M${baseRight - 13} ${groundY} h10 v-5 h-10 z M${baseRight + 3} ${groundY} h10 v-5 h-10 z`}
        />
        <path className="hbB-leader" d={`M${baseRight - 10} ${groundY - 4} q-16 -4 -44 -6`} />
        <text className="hbB-soft" x="36" y="226">
          {en ? 'anti-slip feet' : 'protiskluzové patky'}
        </text>
        {/* h = 4 parts */}
        <Dim arrow={ids.arrow} x1={205} y1={groundY} x2={205} y2={topY} />
        <line className="hbB-dim" x1="201" y1={topY} x2={wallX} y2={topY} strokeDasharray="3 3" />
        <text className="hbB-dim-txt" x="198" y="150" textAnchor="end">
          {en ? 'h = 4 parts' : 'h = 4 díly'}
        </text>
        {/* 1 part out */}
        <Dim arrow={ids.arrow} x1={baseRight} y1={252} x2={wallX} y2={252} />
        <line
          className="hbB-dim"
          x1={baseRight}
          y1={groundY + 4}
          x2={baseRight}
          y2="256"
          strokeDasharray="3 3"
        />
        <text className="hbB-dim-txt" x={wallX + 18} y="258">
          {en ? '1 part (h ⁄ 4)' : '1 díl (h ⁄ 4)'}
        </text>
        {/* overhang above the landing */}
        <Dim arrow={ids.arrow} x1={330} y1={topY} x2={330} y2={landingY} />
        <line className="hbB-dim" x1={wallX} y1={topY} x2="336" y2={topY} strokeDasharray="3 3" />
        <text className="hbB-dim-txt" x="338" y="78">
          {en ? '≥ 1,1 m overhang' : '≥ 1,1 m přesah'}
        </text>
        {/* angle */}
        <path className="hbB-dim" d={arc(baseRight)} />
        <text className="hbB-dim-txt" x={baseRight - 6} y={groundY - 12} textAnchor="end">
          75°
        </text>
        <Verdict kind="ok" x={60} y={165} />
      </On>

      {/* 3 - too shallow */}
      <On step="3">
        <line className="hbB-ghost" x1={baseRight} y1={groundY} x2={wallX} y2={topY} />
        <Ladder x1={baseFlat} y1={groundY} x2={wallX} y2={topY} />
        <path className="hbB-dim" d={arc(baseFlat)} />
        <text className="hbB-no-txt" x={baseFlat + 26} y={groundY - 60} textAnchor="end">
          ≈ 55°
        </text>
        {/* foot slides out */}
        <path className="hbB-no" d={`M${baseFlat - 14} ${groundY - 6} h-40 m10 -8 l-10 8 l10 8`} />
        <text className="hbB-no-txt" x="44" y={groundY - 36}>
          {en ? 'foot slides out' : 'pata podklouzne'}
        </text>
        <Verdict kind="no" x={60} y={165} />
      </On>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Five safety rules (ČSN EN 50110-1) as a numbered checklist beside a switchboard; step k rings rule k. */
export function LockoutTagoutScene({ en }: SceneProps) {
  const ids = blueprintIds('lockoutTagout');
  const rules = en
    ? ['Switch off', 'Lock out', 'Test dead', 'Earth & short', 'Cover / fence off']
    : ['Vypni', 'Zajisti', 'Ověř beznapěťový stav', 'Uzemni a zkratuj', 'Zakryj / ohraď'];
  const rowY = (i: number) => 44 + i * 38;
  const later = (i: number) =>
    Array.from({ length: 5 - i - 1 }, (_, k) => `${i + 2 + k}`).join(' ');
  return (
    <Blueprint
      id="lockoutTagout"
      label={
        en
          ? 'Five safety rules before electrical work'
          : 'Pět bezpečnostních pravidel před prací na elektrickém zařízení'
      }
      title={en ? 'Five rules · de-energised work' : 'Pět pravidel · práce bez napětí'}
      source="ČSN EN 50110-1 · NV 194/2022 Sb."
    >
      {/* switchboard */}
      <rect className="hbB-card" x="30" y="44" width="140" height="200" rx="4" />
      <rect className="hbB-cell" x="70" y="62" width="60" height="44" rx="2" />
      <line className="hbB-thin" x1="100" y1="66" x2="100" y2="72" />
      <text className="hbB-mono" x="140" y="90">
        0
      </text>
      {/* terminals: three phases + a neighbouring live part */}
      <g className="hbB-ink">
        <circle cx="60" cy="196" r="4" />
        <circle cx="90" cy="196" r="4" />
        <circle cx="120" cy="196" r="4" />
      </g>
      <path className="hbB-bolt" d="M154 180 l-7 12 h6 l-5 12 l12 -15 h-6 l5 -9 z" />

      {/* 1 - lever OFF */}
      <On step="1 2 3 4 5">
        <line className="hbB-draw-heavy" x1="100" y1="84" x2="100" y2="104" />
        <circle className="hbB-ink" cx="100" cy="84" r="4" />
      </On>
      {/* 2 - padlock + tag */}
      <On step="2 3 4 5">
        <g className="hbA-lock">
          <path className="hbB-draw" d="M92 122 v-8 a8 8 0 0 1 16 0 v8" />
          <rect className="hbB-ink" x="88" y="122" width="24" height="18" rx="2" />
        </g>
        <path className="hbB-leader" d="M112 132 h10" />
        <rect className="hbB-head" x="52" y="148" width="98" height="24" rx="2" />
        <circle className="hbB-ink-faint" cx="60" cy="160" r="2.5" />
        <text className="hbB-lbl" x="106" y="166" textAnchor="middle">
          {en ? 'LOCKED OUT' : 'NEZAPÍNAT'}
        </text>
      </On>
      {/* 3 - tester across two phases reads 0 V */}
      <On step="3">
        <rect className="hbB-cell" x="34" y="212" width="56" height="24" rx="2" />
        <text className="hbB-mono" x="62" y="229" textAnchor="middle">
          0 V
        </text>
        <line className="hbB-thin" x1="50" y1="212" x2="60" y2="200" />
        <line className="hbB-thin" x1="74" y1="212" x2="90" y2="200" />
      </On>
      {/* 4 - earth + short-circuit bar */}
      <On step="4 5">
        <line className="hbB-draw-heavy" x1="56" y1="196" x2="124" y2="196" />
        <line className="hbB-draw" x1="90" y1="196" x2="90" y2="214" />
        <line className="hbB-draw" x1="78" y1="215" x2="102" y2="215" />
        <line className="hbB-draw" x1="83" y1="221" x2="97" y2="221" />
        <line className="hbB-draw" x1="87" y1="227" x2="93" y2="227" />
      </On>
      {/* 5 - barrier over the neighbouring live part */}
      <On step="5">
        <rect x="138" y="176" width="28" height="36" fill={`url(#${ids.hatch})`} />
        <rect className="hbB-lim" x="138" y="176" width="28" height="36" />
      </On>

      {/* checklist */}
      {rules.map((rule, i) => (
        <g key={rule}>
          <text className="hbB-num" x="196" y={rowY(i) + 27}>
            {i + 1}
          </text>
          <text className="hbB-lbl" x="222" y={rowY(i) + 24}>
            {rule}
          </text>
          <On step={`${i + 1}`}>
            <rect className="hbB-focus" x="186" y={rowY(i)} width="278" height="34" rx="4" />
            <Verdict kind="ok" x={447} y={rowY(i) + 17} r={11} />
          </On>
          {i < 4 && (
            <On step={later(i)}>
              <Sq kind="ok" x={447} y={rowY(i) + 17} />
            </On>
          )}
        </g>
      ))}
      <text className="hbB-cap" x="222" y="250">
        {en ? 'in this order - always' : 'v tomto pořadí - vždy'}
      </text>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Gas leak: smell → no switches / phone / flame → ventilate, close the main valve, call 1239. */
export function GasLeakScene({ en }: SceneProps) {
  const ids = blueprintIds('gasLeak');
  return (
    <Blueprint
      id="gasLeak"
      label={en ? 'Gas leak - what to do' : 'Únik plynu - postup'}
      title={en ? 'Gas leak · procedure' : 'Únik plynu · postup'}
      source="NV 191/2022 Sb. · pohotovost 1239"
    >
      {/* room: floor, left wall with a window, gas pipe with the main valve */}
      <Solid hatch={ids.hatch} x={24} y={224} width={432} height={12} edge="top" />
      <Solid hatch={ids.hatch} x={24} y={40} width={10} height={184} edge="right" />
      <rect className="hbB-cell" x="44" y="58" width="60" height="62" />
      <line className="hbB-thin" x1="74" y1="58" x2="74" y2="120" />
      <line className="hbB-thin" x1="44" y1="89" x2="104" y2="89" />
      {/* appliance */}
      <rect className="hbB-card" x="120" y="166" width="84" height="58" rx="2" />
      <circle className="hbB-draw" cx="142" cy="182" r="7" />
      <circle className="hbB-draw" cx="182" cy="182" r="7" />
      <path className="hbB-draw" d="M204 200 h18" />
      <circle className="hbB-ink" cx="222" cy="200" r="3" />
      {/* pipe + main valve (HUP) */}
      <line className="hbB-draw-heavy" x1="222" y1="200" x2="400" y2="200" />
      <line className="hbB-draw-heavy" x1="400" y1="200" x2="400" y2="70" />
      <circle className="hbB-card" cx="400" cy="130" r="12" />
      <text className="hbB-soft" x="418" y="136">
        HUP
      </text>

      {/* 1 - leak at the joint */}
      <On step="1 2">
        <g className="hbB-ghost">
          <path className="hbA-waft" d="M228 194 c6 -8 -4 -14 2 -22" />
          <path className="hbA-waft" d="M238 196 c6 -8 -4 -14 2 -22" />
          <path className="hbA-waft" d="M248 194 c6 -8 -4 -14 2 -22" />
        </g>
        <line className="hbB-draw" x1="400" y1="118" x2="400" y2="142" />
      </On>
      <On step="1">
        <path className="hbB-leader" d="M236 168 q10 -30 40 -40" />
        <text className="hbB-no-txt" x="280" y="126">
          {en ? 'leak · act now' : 'únik · jednej'}
        </text>
        <text className="hbB-cap" x="280" y="148">
          {en ? 'odorised (THT)' : 'odorant THT'}
        </text>
      </On>

      {/* 2 - no ignition sources */}
      <On step="2">
        {/* light switch */}
        <g transform="translate(70 130)">
          <circle className="hbB-card" cx="30" cy="0" r="24" />
          <rect className="hbB-draw" x="20" y="-12" width="20" height="24" rx="2" />
          <rect className="hbB-ink" x="25" y="-6" width="10" height="7" />
          <path
            className="hbB-pict-red"
            d="M30 0 m-24 0 a24 24 0 1 0 48 0 a24 24 0 1 0 -48 0 M13 -17 L47 17"
          />
        </g>
        {/* phone */}
        <g transform="translate(150 130)">
          <circle className="hbB-card" cx="30" cy="0" r="24" />
          <rect className="hbB-draw" x="22" y="-14" width="16" height="28" rx="3" />
          <path
            className="hbB-pict-red"
            d="M30 0 m-24 0 a24 24 0 1 0 48 0 a24 24 0 1 0 -48 0 M13 -17 L47 17"
          />
        </g>
        {/* flame */}
        <g transform="translate(230 130)">
          <circle className="hbB-card" cx="30" cy="0" r="24" />
          <path
            className="hbB-flame"
            d="M30 10 c-8 -5 -9 -13 -3 -18 c1 5 4 6 4 6 c-1 -7 4 -11 6 -13 c0 6 4 9 5 12 c2 8 -4 14 -12 13 z"
          />
          <path
            className="hbB-pict-red"
            d="M30 0 m-24 0 a24 24 0 1 0 48 0 a24 24 0 1 0 -48 0 M13 -17 L47 17"
          />
        </g>
        <text className="hbB-no-txt" x="100" y="90" textAnchor="middle">
          {en ? 'switch' : 'vypínač'}
        </text>
        <text className="hbB-no-txt" x="180" y="90" textAnchor="middle">
          {en ? 'phone' : 'mobil'}
        </text>
        <text className="hbB-no-txt" x="260" y="90" textAnchor="middle">
          {en ? 'flame' : 'oheň'}
        </text>
        <Verdict kind="no" x={340} y={130} />
      </On>

      {/* 3 - ventilate, close the valve, call 1239 */}
      <On step="3">
        {/* window sash swung open + air arrows */}
        <path className="hbB-draw" d="M44 58 l30 -14 v76 l-30 -14" />
        <path
          className="hbB-dim"
          d="M110 74 h28 m-8 -6 l8 6 l-8 6 M110 100 h28 m-8 -6 l8 6 l-8 6"
        />
        <text className="hbB-ok-txt" x="52" y="146">
          {en ? 'ventilate' : 'vyvětrej'}
        </text>
        {/* valve closed: handle across the pipe */}
        <line className="hbB-draw" x1="386" y1="130" x2="414" y2="130" />
        <path className="hbB-dim" d="M424 112 a26 26 0 0 1 0 36 m0 -36 l-8 4 m8 -4 l2 8" />
        <text className="hbB-ok-txt" x="418" y="166">
          {en ? 'closed' : 'zavři'}
        </text>
        {/* call */}
        <text className="hbB-big" x="250" y="110" textAnchor="middle">
          1239
        </text>
        <text className="hbB-soft" x="250" y="132" textAnchor="middle">
          {en ? 'leave · call' : 'opusť prostor · volej'}
        </text>
        <Verdict kind="ok" x={330} y={78} />
      </On>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Atmosphere test before entry: reading table O₂ / LEL against limits; safe ✓ vs. oxygen-deficient ✗. */
export function AtmosphereTestScene({ en }: SceneProps) {
  const ids = blueprintIds('atmosphereTest');
  const cols = { x: [230, 280, 388], w: [50, 108, 76] };
  const rowH = 38;
  const headY = 44;
  return (
    <Blueprint
      id="atmosphereTest"
      label={
        en
          ? 'Atmosphere test before entering a confined space'
          : 'Měření atmosféry před vstupem do uzavřeného prostoru'
      }
      title={en ? 'Atmosphere test · before entry' : 'Měření atmosféry · před vstupem'}
      source="NV 101/2005 Sb. · vyhl. 180/2015 Sb."
    >
      {/* tank cross-section */}
      <Solid hatch={ids.hatch} x={40} y={80} width={12} height={160} edge="right" />
      <Solid hatch={ids.hatch} x={196} y={80} width={12} height={160} edge="left" />
      <Solid hatch={ids.hatch} x={40} y={228} width={168} height={12} edge="top" />
      <line className="hbB-draw-heavy" x1="52" y1="80" x2="96" y2="80" />
      <line className="hbB-draw-heavy" x1="152" y1="80" x2="196" y2="80" />
      <text className="hbB-soft" x="124" y="66" textAnchor="middle">
        {en ? 'hatch' : 'vstup'}
      </text>
      {/* sample heights */}
      <g className="hbB-ghost">
        <line x1="60" y1="120" x2="188" y2="120" />
        <line x1="60" y1="170" x2="188" y2="170" />
        <line x1="60" y1="218" x2="188" y2="218" />
      </g>
      {/* meter lowered on a line */}
      <g className="hbA-lower">
        <line className="hbB-thin" x1="124" y1="40" x2="124" y2="150" />
        <rect className="hbB-card" x="106" y="150" width="36" height="46" rx="3" />
        <On step="1">
          <circle className="hbB-ok-fill" cx="124" cy="164" r="5" />
        </On>
        <On step="2">
          <circle className="hbA-alert hbB-no-fill" cx="124" cy="164" r="5" />
        </On>
        <line className="hbB-thin" x1="114" y1="180" x2="134" y2="180" />
        <line className="hbB-thin" x1="114" y1="187" x2="134" y2="187" />
      </g>

      {/* reading table */}
      <rect className="hbB-head" x={cols.x[0]} y={headY} width={cols.w[0]} height="30" />
      <rect className="hbB-head" x={cols.x[1]} y={headY} width={cols.w[1]} height="30" />
      <rect className="hbB-head" x={cols.x[2]} y={headY} width={cols.w[2]} height="30" />
      <text className="hbB-soft" x={cols.x[1] + 8} y={headY + 21}>
        {en ? 'limit' : 'limit'}
      </text>
      <text className="hbB-soft" x={cols.x[2] + 8} y={headY + 21}>
        {en ? 'reading' : 'odečet'}
      </text>
      {[
        { k: 'O₂', lim: '19,5-23,5 %' },
        { k: 'LEL', lim: '< 10 %' },
      ].map((r, i) => {
        const y = headY + 30 + i * rowH;
        return (
          <g key={r.k}>
            <rect className="hbB-cell" x={cols.x[0]} y={y} width={cols.w[0]} height={rowH} />
            <rect className="hbB-cell" x={cols.x[1]} y={y} width={cols.w[1]} height={rowH} />
            <rect className="hbB-cell" x={cols.x[2]} y={y} width={cols.w[2]} height={rowH} />
            <text className="hbB-lbl" x={cols.x[0] + 8} y={y + 25}>
              {r.k}
            </text>
            <text className="hbB-mono" x={cols.x[1] + 8} y={y + 25}>
              {r.lim}
            </text>
          </g>
        );
      })}
      {/* readings per step */}
      <On step="1">
        <text className="hbB-ok-txt" x={cols.x[2] + 8} y={headY + 55}>
          20,9 %
        </text>
        <text className="hbB-ok-txt" x={cols.x[2] + 8} y={headY + 55 + rowH}>
          0 %
        </text>
        <Verdict kind="ok" x={250} y={190} />
        <text className="hbB-ok-txt" x="274" y="196">
          {en ? 'safe to enter' : 'vstup povolen'}
        </text>
      </On>
      <On step="2">
        <text className="hbB-no-txt" x={cols.x[2] + 8} y={headY + 55}>
          17,0 %
        </text>
        <rect
          className="hbB-focus"
          x={cols.x[2] - 2}
          y={headY + 28}
          width={cols.w[2] + 4}
          height={rowH + 4}
          rx="3"
        />
        <text className="hbB-ok-txt" x={cols.x[2] + 8} y={headY + 55 + rowH}>
          0 %
        </text>
        <Verdict kind="no" x={250} y={190} />
        <text className="hbB-no-txt" x="274" y="196">
          {en ? 'do not enter' : 'nevstupuj'}
        </text>
        <text className="hbB-cap" x="274" y="220">
          {en ? 'ventilate · re-test' : 'větrej · měř znovu'}
        </text>
      </On>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Decanting chemicals: labelled original container ✓ vs. a drink bottle ✗ (CLP). */
export function ChemicalBottlesScene({ en }: SceneProps) {
  return (
    <Blueprint
      id="chemicalBottles"
      label={en ? 'Labelled container versus drink bottle' : 'Označená nádoba versus láhev od pití'}
      title={en ? 'Chemicals · container' : 'Chemikálie · nádoba'}
      source="nař. CLP (ES) 1272/2008 · z. 350/2011 Sb."
    >
      {/* 1 - original labelled container */}
      <Panel x={16} y={36} width={216} height={212} n={1}>
        <g transform="translate(-20 0)">
          <path
            className="hbB-draw-heavy"
            d="M86 44 h28 v14 l10 8 v80 a6 6 0 0 1 -6 6 h-36 a6 6 0 0 1 -6 -6 v-80 l10 -8 z"
          />
          <rect className="hbB-head" x="80" y="78" width="40" height="52" />
          {/* GHS diamond */}
          <path className="hbB-pict-red" d="M100 84 l10 10 l-10 10 l-10 -10 z" />
          <text className="hbB-mono" x="100" y="124" textAnchor="middle">
            NaOH
          </text>
          <path className="hbB-leader" d="M122 100 h20" />
        </g>
        <text className="hbB-lbl" x="126" y="98">
          {en ? 'label' : 'etiketa'}
        </text>
        <text className="hbB-soft" x="126" y="118">
          {en ? '+ SDS' : '+ bezp. list'}
        </text>
        <On step="1">
          <rect className="hbB-focus" x="-4" y="-4" width="224" height="220" rx="8" />
          <Verdict kind="ok" x={182} y={172} />
        </On>
      </Panel>

      {/* 2 - drink bottle */}
      <Panel x={248} y={36} width={216} height={212} n={2}>
        <path
          className="hbB-draw-heavy"
          d="M92 46 h16 v10 q10 8 10 22 v66 a6 6 0 0 1 -6 6 h-24 a6 6 0 0 1 -6 -6 v-66 q10 -14 10 -22 z"
        />
        <rect className="hbB-head" x="82" y="96" width="36" height="20" />
        <text className="hbB-mono" x="100" y="111" textAnchor="middle">
          ?
        </text>
        <On step="2">
          {/* pouring from an unlabelled source */}
          <path className="hbB-thin hbA-pour" d="M136 44 c-8 6 -24 10 -34 4" />
          <path className="hbB-draw" d="M140 20 h24 l6 26 h-36 z" />
          <line className="hbB-no" x1="70" y1="60" x2="130" y2="146" />
          <text className="hbB-no-txt" x="146" y="98">
            {en ? 'mix-up' : 'záměna'}
          </text>
          <text className="hbB-no-txt" x="146" y="118">
            {en ? 'poisoning' : 'otrava'}
          </text>
          <rect className="hbB-focus" x="-4" y="-4" width="224" height="220" rx="8" />
          <Verdict kind="no" x={182} y={172} />
        </On>
      </Panel>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Lifting: rounded back ✗ vs. squat, straight back, load close ✓; NV 361/2007 Sb. limits. */
export function LiftTechniqueScene({ en }: SceneProps) {
  return (
    <Blueprint
      id="liftTechnique"
      label={
        en
          ? 'Lifting technique - rounded back versus straight back'
          : 'Technika zvedání - shrbená versus rovná záda'
      }
      title={en ? 'Lifting · technique' : 'Zvedání břemene · technika'}
      source="NV 361/2007 Sb. § 29, příl. 5"
    >
      {/* 1 - rounded back, straight legs, load far out */}
      <Panel x={16} y={36} width={216} height={180} n={1}>
        <line className="hbB-thin" x1="12" y1="160" x2="204" y2="160" />
        <g className="hbB-draw">
          <line x1="90" y1="118" x2="84" y2="160" />
          <line x1="94" y1="118" x2="104" y2="160" />
          <path d="M92 118 C98 84 130 70 148 82" />
          <line x1="146" y1="86" x2="150" y2="128" />
        </g>
        <circle className="hbB-draw" cx="158" cy="76" r="9" />
        <rect className="hbB-cell" x="136" y="128" width="34" height="26" />
        <On step="1">
          <path className="hbB-no" d="M100 100 C108 78 128 70 142 76" />
          <path className="hbB-no" d="M112 62 l6 14 m-6 -14 l-8 12" />
          <text className="hbB-no-txt" x="12" y="56">
            {en ? 'bent spine' : 'ohnutá páteř'}
          </text>
          {/* below the ground line, under the load box - clear of the figure */}
          <text className="hbB-soft" x="204" y="176" textAnchor="end">
            {en ? 'load far out' : 'břemeno daleko'}
          </text>
          <Verdict kind="no" x={44} y={126} />
        </On>
      </Panel>

      {/* 2 - squat, straight back, load close */}
      <Panel x={248} y={36} width={216} height={180} n={2}>
        <line className="hbB-thin" x1="12" y1="160" x2="204" y2="160" />
        <g className="hbB-draw">
          <path d="M100 116 L128 128 L118 160" />
          <path d="M100 116 L122 134 L112 160" />
          <line x1="100" y1="116" x2="116" y2="72" />
          <line x1="114" y1="76" x2="126" y2="104" />
        </g>
        <circle className="hbB-draw" cx="120" cy="62" r="9" />
        <rect className="hbB-cell" x="118" y="104" width="32" height="26" />
        <On step="2">
          <line className="hbB-ok" x1="102" y1="112" x2="116" y2="76" />
          <text className="hbB-ok-txt" x="12" y="60">
            {en ? 'straight back' : 'rovná záda'}
          </text>
          <text className="hbB-soft" x="12" y="82">
            {en ? 'load close' : 'břemeno u těla'}
          </text>
          <text className="hbB-soft" x="12" y="104">
            {en ? 'knees bent' : 'pokrčená kolena'}
          </text>
          <Verdict kind="ok" x={44} y={134} />
        </On>
      </Panel>

      {/* limits row */}
      <rect className="hbB-cell" x="16" y="226" width="448" height="28" rx="2" />
      <text className="hbB-mono" x="26" y="245">
        {en
          ? 'limit: men 50 kg · women 20 kg (occasional)'
          : 'limit: muži 50 kg · ženy 20 kg (občasně)'}
      </text>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** After welding: sparks end (t = 0), the fire watch runs ≥ 8 h, a hidden ember is caught. */
export function FireWatchScene({ en }: SceneProps) {
  const ids = blueprintIds('fireWatch');
  const axisY = 190;
  const t0 = 80;
  const t8 = 400;
  return (
    <Blueprint
      id="fireWatch"
      label={
        en ? 'Fire watch after welding - timeline' : 'Požární dohled po svařování - časová osa'
      }
      title={en ? 'Fire watch · after welding' : 'Požární dohled · po svařování'}
      source="vyhl. 87/2000 Sb. § 4 · ČSN 05 0601"
    >
      {/* timeline */}
      <line className="hbB-draw" x1="40" y1={axisY} x2="440" y2={axisY} />
      {[0, 2, 4, 6, 8].map((h) => {
        const x = t0 + (h / 8) * (t8 - t0);
        return (
          <g key={h}>
            <line className="hbB-draw" x1={x} y1={axisY - 5} x2={x} y2={axisY + 5} />
            <text className="hbB-mono" x={x} y={axisY + 24} textAnchor="middle">
              {h === 0 || h === 8 ? `${h} h` : h}
            </text>
          </g>
        );
      })}
      {/* partition with a cavity behind it */}
      <Solid hatch={ids.hatch} x={330} y={100} width={12} height={axisY - 100} edge="left" />
      <line className="hbB-thin" x1="342" y1="100" x2="342" y2={axisY} />

      {/* 1 - welding ends */}
      <On step="1">
        <path className="hbB-draw-heavy" d="M50 150 l22 -12" />
        <rect className="hbB-ink" x="40" y="146" width="14" height="12" rx="2" />
        <g className="hbB-lim">
          <line className="hbA-spark" x1="80" y1="132" x2="88" y2="124" />
          <line className="hbA-spark" x1="84" y1="140" x2="96" y2="138" />
          <line className="hbA-spark" x1="78" y1="122" x2="80" y2="112" />
        </g>
        <text className="hbB-lbl" x="110" y="120">
          {en ? 'welding ends' : 'svařování končí'}
        </text>
        <text className="hbB-cap" x="110" y="142">
          {en ? 'risk does not' : 'riziko trvá'}
        </text>
      </On>

      {/* 2 - the watch runs ≥ 8 h */}
      <On step="2 3">
        <rect x={t0} y={axisY - 12} width={t8 - t0} height="12" fill={`url(#${ids.hatch})`} />
        <Dim arrow={ids.arrow} x1={t0} y1={236} x2={t8} y2={236} />
        <text className="hbB-dim-txt" x="240" y="256" textAnchor="middle">
          {en ? '≥ 8 h fire watch' : '≥ 8 h požární dohled'}
        </text>
      </On>
      <On step="2">
        <circle className="hbB-card" cx="240" cy="130" r="34" />
        <g className="hbB-thin">
          <line x1="240" y1="98" x2="240" y2="104" />
          <line x1="272" y1="130" x2="266" y2="130" />
          <line x1="240" y1="162" x2="240" y2="156" />
          <line x1="208" y1="130" x2="214" y2="130" />
        </g>
        <line className="hbB-draw-heavy hbA-clock-hand" x1="240" y1="130" x2="240" y2="106" />
        <line className="hbB-draw" x1="240" y1="130" x2="256" y2="130" />
        <circle className="hbB-ink" cx="240" cy="130" r="2.5" />
        <text className="hbB-lbl" x="240" y="84" textAnchor="middle">
          {en ? 'watch keeps running' : 'dohled běží dál'}
        </text>
      </On>

      {/* 3 - hidden smoulder found */}
      <On step="3">
        <path
          className="hbB-flame hbA-spark"
          d="M364 186 c-8 -5 -9 -13 -3 -18 c1 5 4 6 4 6 c-1 -7 4 -11 6 -13 c0 6 4 9 5 12 c2 8 -4 14 -12 13 z"
        />
        <text className="hbB-no-txt" x="322" y="100" textAnchor="end">
          {en ? 'hidden smoulder' : 'skryté doutnání'}
        </text>
        <text className="hbB-ok-txt" x="322" y="122" textAnchor="end">
          {en ? 'caught in time' : 'zachyceno včas'}
        </text>
        <Verdict kind="ok" x={420} y={120} />
      </On>
    </Blueprint>
  );
}

/* ------------------------------------------------------------------ */

/** Braking van: a loose toolbox flies into the bulkhead ✗; strapped low against it, it stays ✓. */
export function CargoShiftScene({ en }: SceneProps) {
  const ids = blueprintIds('cargoShift');
  const roadY = 220;
  return (
    <Blueprint
      id="cargoShift"
      label={
        en
          ? 'Loose versus strapped cargo under braking'
          : 'Volný versus upevněný náklad při brzdění'
      }
      title={en ? 'Cargo · braking' : 'Náklad · brzdění'}
      source="z. 361/2000 Sb. § 52"
    >
      {/* road */}
      <Solid hatch={ids.hatch} x={24} y={roadY} width={432} height={12} edge="top" />
      {/* van: cargo box + cab, driving right */}
      <rect className="hbB-card" x="60" y="80" width="240" height="120" />
      <path className="hbB-card" d="M300 200 V110 H352 L392 150 V200 Z" />
      <line className="hbB-draw-heavy" x1="300" y1="80" x2="300" y2="200" />
      <circle className="hbB-card" cx="120" cy="204" r="16" />
      <circle className="hbB-card" cx="360" cy="204" r="16" />
      {/* braking: velocity arrow + skid marks */}
      <path className="hbB-dim" d="M180 60 h80 m-8 -6 l8 6 l-8 6" />
      <text className="hbB-dim-txt" x="172" y="66" textAnchor="end">
        {en ? 'braking' : 'brzdění'}
      </text>
      <g className="hbB-thin">
        <line x1="70" y1={roadY - 2} x2="104" y2={roadY - 2} />
        <line x1="310" y1={roadY - 2} x2="344" y2={roadY - 2} />
      </g>
      <text className="hbB-soft" x="306" y="98">
        {en ? 'bulkhead' : 'přepážka'}
      </text>

      {/* 1 - loose toolbox slides into the bulkhead */}
      <On step="1">
        <g className="hbA-slide">
          <rect className="hbB-ink" x="112" y="176" width="36" height="24" rx="2" />
        </g>
        <path className="hbB-no" d="M160 160 h80 m-8 -6 l8 6 l-8 6" />
        <path className="hbB-no" d="M296 150 l-10 -8 l6 12 l-12 -2 l8 8 l-10 4 l12 2" />
        <text className="hbB-mono" x="72" y="104">
          20 kg × 50 km/h
        </text>
        <text className="hbB-no-txt" x="72" y="126">
          {en ? '≈ 0,5 t impact' : '≈ 0,5 t náraz'}
        </text>
        <Verdict kind="no" x={272} y={108} />
      </On>

      {/* 2 - strapped low against the bulkhead */}
      <On step="2">
        <rect className="hbB-ink" x="258" y="176" width="36" height="24" rx="2" />
        <path className="hbB-ok" d="M246 200 L258 176 H294 L300 198" />
        <circle className="hbB-ok-fill" cx="246" cy="199" r="3" />
        <path className="hbB-leader" d="M262 172 q-30 -20 -60 -30" />
        <text className="hbB-ok-txt" x="68" y="104">
          {en ? 'strap or net' : 'popruh nebo síť'}
        </text>
        <text className="hbB-soft" x="68" y="126">
          {en ? 'heavy low, at bulkhead' : 'těžké dolů, k přepážce'}
        </text>
        <Verdict kind="ok" x={272} y={108} />
      </On>
    </Blueprint>
  );
}
